import { IOrder } from './orders.interface';

export const ORDER_STATUS = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  processing: 'processing',
  processed: 'processed',
  shipped: 'shipped',
  delivered: 'delivered',
  cancelled: 'cancelled',
  approved: 'approved',
};

export enum ORDER_STATUS_TYPE {
  submitted = 'neutral',
  in_review = 'neutral',
  processing = 'neutral',
  processed = 'neutral',
  draft = 'neutral',
  shipped = 'accent',
  delivered = 'positive',
  cancelled = 'negative',
  approved = 'neutral',
}

export const CARD_PRODUCT_TYPE = {
  generic: 'generic',
  fuel_only: 'fuel_only',
  contactless_card: 'contactless_card',
};

export const ORDER_STATUS_DROPDOWN = [
  {
    label: $localize`Draft`,
    value: ORDER_STATUS.draft,
  },
  {
    label: $localize`Processing`,
    value: ORDER_STATUS.processing,
  },
  {
    label: $localize`Shipped`,
    value: ORDER_STATUS.shipped,
  },
  {
    label: $localize`Delivered`,
    value: ORDER_STATUS.delivered,
  },
  {
    label: $localize`Canceled`,
    value: ORDER_STATUS.cancelled,
  },
];

export const ORDER_SORT_DROPDOWN = [
  {
    label: $localize`Date created (latest)`,
    value: 'created_at.desc',
  },
  {
    label: $localize`Date created (oldest)`,
    value: 'created_at.asc',
  },
];

export const I18N_ORDER_STATUS = {
  [ORDER_STATUS.draft]: {
    label: $localize`Draft`,
  },
  [ORDER_STATUS.submitted]: {
    label: $localize`Processing`,
  },
  [ORDER_STATUS.in_review]: {
    label: $localize`Processing`,
  },
  [ORDER_STATUS.processed]: {
    label: $localize`Processing`,
  },
  [ORDER_STATUS.approved]: {
    label: $localize`Processing`,
  },
  [ORDER_STATUS.shipped]: {
    label: $localize`Shipped`,
  },
  [ORDER_STATUS.delivered]: {
    label: $localize`Delivered`,
  },
  [ORDER_STATUS.cancelled]: {
    label: $localize`Canceled`,
  },
};

export const MOTIVE_SUPPORT = $localize`Motive Support`;

export const PROCESSING_STATUS_LIST = ['submitted', 'in_review', 'processed'];

export const ORDER_CANCELLATION_REASONS = {
  documents_incomplete: 'documents_incomplete',
  documents_unreadable: 'documents_unreadable',
  documents_invalid: 'documents_invalid',
  documents_altered: 'documents_altered',
  shipping_address_invalid: 'shipping_address_invalid',
  payment_due: 'payment_due',
  customer_unresponsive: 'customer_unresponsive',
};

interface IOrderStatusTransitions {
  draft?: string;
  submitted: string;
  processing: string;
  processed: string;
  shipped: string;
  delivered: string;
}

export const ORDER_STATUS_TRANSITIONS: IOrderStatusTransitions = {
  draft: $localize`Order placed`,
  submitted: $localize`Order placed`,
  processing: $localize`Processing`,
  processed: $localize`Processed`,
  shipped: $localize`Shipped`,
  delivered: $localize`Delivered`,
};

export const CARD_ENTITIES = {
  VEHICLE: 'vehicle',
  DRIVER: 'driver',
};

export const orderStatusTitleMap = (orderData: IOrder) => {
  return {
    [ORDER_STATUS.draft]: ORDER_STATUS_TRANSITIONS.draft,
    [ORDER_STATUS.submitted]: ORDER_STATUS_TRANSITIONS.submitted,
    [ORDER_STATUS.processed]: (orderData.status === ORDER_STATUS.submitted ||
      orderData.status === ORDER_STATUS.in_review)
        ? ORDER_STATUS_TRANSITIONS.processing
        : ORDER_STATUS_TRANSITIONS.processed,
    [ORDER_STATUS.shipped]: ORDER_STATUS_TRANSITIONS.shipped,
    [ORDER_STATUS.delivered]: ORDER_STATUS_TRANSITIONS.delivered,
  };
};

export const STATUS_PRIORITY = {
  [ORDER_STATUS.draft]: 1,
  [ORDER_STATUS.submitted]: 1,
  [ORDER_STATUS.in_review]: 1,
  [ORDER_STATUS.approved]: 1,
  [ORDER_STATUS.processed]: 2,
  [ORDER_STATUS.shipped]: 3,
  [ORDER_STATUS.delivered]: 4,
};

export const REQUIRED_STATUS_FOR_PROCESS = {
  [ORDER_STATUS.draft]: STATUS_PRIORITY[ORDER_STATUS.draft],
  [ORDER_STATUS.processed]: STATUS_PRIORITY[ORDER_STATUS.submitted], // Processed requires at least submitted or in_review
  [ORDER_STATUS.shipped]: STATUS_PRIORITY[ORDER_STATUS.processed], // Shipped requires at least processed
  [ORDER_STATUS.delivered]: STATUS_PRIORITY[ORDER_STATUS.shipped], // Delivered requires at least shipped
};

export const CARD_ORDER_EVENTS = {
  ORDER_CARDS_BTN_CLICKED: 'New Card Order - Order Cards Button Clicked',
  CARD_DETAILS_PAGE_LOADED: 'New Card Order - Card Details - Page Loaded',
  CARD_DETAILS_NUMBER_OF_CARDS_ENTERED: 'New Card Order - Card Details - Number of Cards Entered',
  CARD_DETAILS_CONTINUE_BTN_CLICKED: 'New Card Order - Card Details - Continue Button Clicked',
  CARD_DETAILS_CLOSE_BTN_CLICKED: 'New Card Order - Card Details - Close Button Clicked',
  CUSTOMISE_CARD_PAGE_LOADED: 'New Card Order - Customise Card - Page Loaded',
  CUSTOM_CSV_OPTION_SELECTED: 'New Card Order - Custom Card - CSV Option Selected',
  CUSTOM_CARD_SUGGESTION_SELECTED: 'New Card Order - Custom Card - Suggestion Selected',
  CUSTOM_CARD_CLOSE_BTN_CLICKED: 'New Card Order - Custom Card - Close Button Clicked',
  CUSTOM_CARD_GO_BACK_BTN_CLICKED: 'New Card Order - Custom Card - Go Back Button Clicked',
  CUSTOM_CARD_CONTINUE_BTN_CLICKED: 'New Card Order - Custom Card - Continue Button Clicked',
  SHIPPING_ADDRESS_EXISTING_ADDRESS_SELECTED: 'New Card Order - Shipping Address - Existing Address Selected',
  SHIPPING_ADDRESS_NEW_ADDRESS_SELECTED: 'New Card Order - Shipping Address - New Address Selected',
  SHIPPING_ADDRESS_CHOOSE_FILE_SELECTED: 'New Card Order - Shipping Address - Choose File Selected',
  SHIPPING_ADDRESS_FILE_UPLOADED: 'New Card Order - Shipping Address - File Uploaded',
  SHIPPING_ADDRESS_GO_BACK_BTN_CLICKED: 'New Card Order - Shipping Address - Go Back Button Clicked',
  SHIPPING_ADDRESS_PLACE_ORDER_BTN_CLICKED: 'New Card Order - Shipping Address - Place Order Clicked',
  SHIPPING_ADDRESS_CLOSE_BTN_CLICKED: 'New Card Order - Shipping Address - Close Button Clicked',
  ORDER_CONFIRMATION_PAGE_LOADED: 'New Card Order - Shipping Address - Order Confirmation Page Loaded',
  ORDERS_PAGE_LOADED: 'Orders - Page Loaded',
  ORDER_DETAILS_BTN_CLICKED: 'Orders - Order Details Button Clicked',
  ORDER_DETAILS_PAGE_LOADED: 'Orders - Order Details Page Loaded',
  ORDER_TRACK_SHIPMENT_SELECTED: 'Orders - Track Shipment Selected',
  BULK_ENTITY_SELECT_CONTINUE_BTN_CLICKED: 'New Card Order - Bulk Entity Select - Continue Button Clicked',
  BULK_ENTITY_SELECT_BACK_BTN_CLICKED: 'New Card Order - Bulk Entity Select - Back Button Clicked',
};

export const CSV_VALIDATION_ERROR_MAP: { [key: string]: string } = {
  parse_error: $localize `Unable to read the uploaded file. Please check the format and try again.`,
  incorrect_headers: $localize `The uploaded file doesn’t match the required format. Download the template, format your data accordingly, and try importing again.`,
  no_data_error: $localize `The uploaded file contains no data. Please check the file and try again.`,
};

export const DEBOUNCE_DELAY: number = 400;